import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ACCESS_LEVELS } from "@/lib/enums";
import { AccessLevelSelect } from "./AccessLevelSelect";

const accessGrantFormSchema = z.object({
  email: z.string().trim().pipe(z.email("Enter a valid email address")),
  level: z.enum(ACCESS_LEVELS),
  title: z.string().trim().max(80, "Title must be 80 characters or fewer"),
});

export type AccessGrantFormValues = z.infer<typeof accessGrantFormSchema>;

/**
 * The one form behind both "Add person" and "Edit access". In edit mode the
 * email is shown but fixed: changing it would be a different person, which is
 * a remove plus an add.
 */
export function AccessGrantForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSaving,
  submitLabel,
  emailLocked = false,
  levelLocked = false,
}: {
  defaultValues?: Partial<AccessGrantFormValues>;
  onSubmit: (values: AccessGrantFormValues) => void;
  onCancel: () => void;
  isSaving: boolean;
  submitLabel: string;
  emailLocked?: boolean;
  levelLocked?: boolean;
}) {
  const form = useForm<AccessGrantFormValues>({
    resolver: zodResolver(accessGrantFormSchema),
    defaultValues: { email: "", level: "aggregate", title: "", ...defaultValues },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Work email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="name@nymbl.app"
                  autoComplete="off"
                  disabled={emailLocked}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="level"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Access level</FormLabel>
              <AccessLevelSelect
                value={field.value}
                onValueChange={field.onChange}
                disabled={levelLocked}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title (optional)</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Client Partner" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : submitLabel}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
